import { Link } from "react-router-dom";
import vector1 from "../../assets/Vector (1).svg";
import { useWishlist } from "../../context/WishlistContext";
import { useProducts } from "../../context/ProductsContext";

const serif = "'Playfair Display', Georgia, serif";
const sans = "'Poppins', 'Segoe UI', sans-serif";

/* ✅ wishlist heart colors: blue outline at rest → solid red when liked */
const HEART_BLUE = "#3436A4";
const HEART_RED  = "#E02020";

/* ---------- icons (matched to Figma originals) ---------- */

const Spark = () => (
  <svg width="13" height="13" viewBox="0 0 12.75 12.75" fill="none" aria-hidden="true">
    <path
      d="M6.375 0C6.375 3.521 3.521 6.375 0 6.375C3.521 6.375 6.375 9.229 6.375 12.75C6.375 9.229 9.229 6.375 12.75 6.375C9.229 6.375 6.375 3.521 6.375 0Z"
      stroke="#CCA466" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

/* ✅ blue outline when not liked → solid RED (fill + stroke) when liked */
const Heart = ({ filled }) => (
  <svg
    viewBox="-1 -1 21 18.5"
    fill={filled ? HEART_RED : "none"}
    aria-hidden="true"
    style={{ transition: "fill .25s ease" }}
  >
    <path
      d="M3.355 0.768C1.334 1.535 0 3.455 0 5.597C0 7.76 1.425 9.358 2.85 10.769L8.083 15.782C8.446 16.185 8.966 16.413 9.512 16.41C10.057 16.407 10.575 16.171 10.933 15.764L16.15 10.769C17.575 9.358 19 7.75 19 5.597C19.01 3.45 17.677 1.521 15.651 0.752C13.626 -0.016 11.331 0.537 9.889 2.14C9.788 2.247 9.647 2.91 9.5 2.91C9.353 2.91 9.212 2.247 9.112 2.14C7.665 0.547 5.376 0.001 3.355 0.768Z"
      stroke={filled ? HEART_RED : HEART_BLUE}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: "stroke .25s ease" }}
    />
  </svg>
);

const BoxIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#CCA466" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
    <path d="m7.5 4.27 9 5.15" />
  </svg>
);

const CalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#CCA466" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 17h.01M12 17h.01M16 17h.01" />
  </svg>
);

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
);

/* ---------- card ---------- */
function BestSellerCard({ p }) {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const liked = isInWishlist(p.id);
  return (
    <Link to={`/products/${p.id}`} className="bs-card">
      {/* media */}
      <div className="bs-media" style={{ backgroundColor: "#F5F3F6" }}>
        <span className="bs-badge"><Spark /> BEST SELLER</span>
        <button
          type="button"
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
          aria-pressed={liked}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(p.id); }}
          className="bs-wish"
        >
          <Heart filled={liked} />
        </button>

        {/* image: rest 1.065 → hover 1 (zoom out) */}
        <img className="bs-img" src={p.image} alt={p.title} loading="lazy" />

        {/* golden arc */}
        <svg className="bs-arc" viewBox="0 -8 371 94" preserveAspectRatio="none" aria-hidden="true">
          <path d="M0 -8C96 67 275 67 371 -8L371 86L0 86Z" fill="#ffffff" />
          <path d="M0 0C96 75 275 75 371 0" stroke="#CCA466" strokeWidth="2.5" fill="none" />
        </svg>
      </div>

      {/* body */}
      <div className="bs-body">
        <h3 className="bs-name">{p.title}</h3>
        <p className="bs-desc">{p.desc}</p>

        <div className="bs-meta">
          <div className="bs-meta-item">
            <span className="bs-meta-ico"><BoxIcon /></span>
            <span>
              <span className="bs-meta-label">MOQ</span>
              <span className="bs-meta-value">{p.moq}</span>
            </span>
          </div>
          <span className="bs-div" />
          <div className="bs-meta-item">
            <span className="bs-meta-ico"><CalIcon /></span>
            <span>
              <span className="bs-meta-label">Lead Time</span>
              <span className="bs-meta-value">{p.lead}</span>
            </span>
          </div>
        </div>

        <span className="bs-cta">
          View Products
          <span className="bs-cta-arrow"><Arrow /></span>
        </span>
      </div>
    </Link>
  );
}

/* ---------- section ---------- */
export default function BestSellers() {
  const { products, loading } = useProducts();

  // Prioritizes Royal Luxury products actually marked "Best Seller" in the
  // CMS, then tops up with other Royal Luxury products so this section
  // isn't sparse while the catalog is still growing.
  const royalLuxuryProducts = products.filter((p) => p.brand === 'Royal Luxury');
  const bestSellers = [...royalLuxuryProducts]
    .sort((a, b) => (a.type === 'Best Seller' ? -1 : 1) - (b.type === 'Best Seller' ? -1 : 1))
    .slice(0, 4);

  if (!loading && bestSellers.length === 0) return null;

  return (
    <section id="shine-best-sellers" className="bs-sec">
      <style>{`
        /* ============ ALL BREAKPOINTS — card is FIXED-SIZE (px) so it renders
           IDENTICAL everywhere; only the NUMBER OF COLUMNS changes:
           ~≥1280 → 4 up · 1024–1279 → 3 up · 640–1023 → 2 up · ≤639 → 1 up ============ */
        .bs-sec{position:relative;width:100%;padding:4.0104vw 0 5.2083vw;background:#FCF9F2;overflow:hidden;}
        .bs-head{text-align:center;max-width:78vw;margin:0 auto 3.2292vw;}
        .bs-eyebrow{color:#E38F2E;font-family:${sans};font-size:0.83vw;font-weight:700;letter-spacing:0.25em;text-transform:uppercase;}
        .bs-title{margin-top:0.5vw;color:#2E3192;font-family:${serif};font-size:1.75vw;font-weight:700;line-height:normal;text-transform:capitalize;}
        .bs-squiggle{display:block;margin-left:34.5vw;margin-right:34.5vw;width:9.25vw;height:auto;}
        .bs-sub{color:#666666;font-family:${sans};font-size:0.97vw;font-weight:500;letter-spacing:0.03em;line-height:1.4583vw;}

        /* ✅ auto-fit grid of FIXED 300px tracks, centered — the browser fits
           as many identical cards per row as the viewport allows */
        .bs-grid{
          display:grid;
          grid-template-columns:repeat(auto-fit, minmax(min(300px, 100%), 300px));
          justify-content:center;
          gap:24px;
          width:100%;
          padding:0 24px;
          box-sizing:border-box;
          margin:0 auto;
        }

        /* ✅ FIXED px card — same width & height in every rendering */
        .bs-card{display:flex;flex-direction:column;width:300px;max-width:100%;
          border-radius:12px;background:#ffffff;overflow:hidden;
          text-decoration:none;box-shadow:0 12px 30px rgba(43,46,126,0.08);
          transition:box-shadow .35s ease;}
        .bs-card:hover{box-shadow:0 22px 44px rgba(43,46,126,0.16);}

        .bs-media{position:relative;display:block;height:260px;overflow:hidden;background:#ffffff;
          border-radius:12px 12px 0 0;}

        .bs-img{position:absolute;inset:0;width:100%;height:100%;object-fit:fill;
          transform:scale(1.065);transform-origin:top center;
          transition:transform .8s cubic-bezier(.22,.61,.36,1);}
        .bs-card:hover .bs-img{transform:scale(1);}

        .bs-arc{position:absolute;left:0;bottom:-24px;width:100%;height:80px;
          display:block;pointer-events:none;
          transition:transform .8s cubic-bezier(.22,.61,.36,1);}
        .bs-card:hover .bs-arc{transform:translateY(-4px);}

        .bs-badge{position:absolute;top:12px;left:12px;z-index:2;display:inline-flex;align-items:center;gap:6px;
          background:#ffffff;border-radius:50vw;padding:6px 12px;color:#CCA466;
          font-family:${sans};font-size:10px;font-weight:500;letter-spacing:0.12em;line-height:1;
          white-space:nowrap;overflow:hidden;}
        .bs-badge svg{flex:0 0 auto;}

        .bs-wish{position:absolute;top:12px;right:12px;z-index:2;width:36px;height:36px;border-radius:50%;
          background:#FAF8F4;border:2px solid rgba(200,155,74,0.5);display:grid;place-items:center;
          cursor:pointer;}
        .bs-wish svg{width:17px;height:15px;display:block;}

        .bs-body{flex:1;display:flex;flex-direction:column;padding:16px 14px 18px;text-align:center;}
        .bs-name{color:#333333;font-family:${serif};font-size:18px;font-weight:500;margin-top:-6px;margin-bottom:8px;}
        .bs-desc{color:#666666;font-family:${sans};font-size:13px;font-weight:400;letter-spacing:0.02em;line-height:20px;
          margin-bottom:14px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}

        .bs-meta{display:flex;align-items:center;justify-content:space-between;gap:10px;
          border-top:1px solid #E8D6BA;padding:10px 2px;}
        .bs-meta-item{display:flex;align-items:center;gap:8px;text-align:left;}
        .bs-meta-item > span > span{display:block;}
        .bs-meta-ico{width:34px;height:34px;border-radius:50%;background:#F7F1E8;display:grid;place-items:center;flex-shrink:0;}
        .bs-meta-ico svg{width:16px;height:16px;}
        .bs-meta-label{color:#CCA466;font-family:${sans};font-size:11px;font-weight:500;}
        .bs-meta-value{color:#333333;font-family:${sans};font-size:12px;font-weight:500;}
        .bs-div{width:1px;height:34px;background:#E4E4E4;}

        .bs-cta{margin-top:14px;display:flex;align-items:center;justify-content:space-between;
          border:2px solid #2E3192;border-radius:50vw;padding:8px 10px 8px 70px;
          color:#2E3192;font-family:${sans};font-size:13px;font-weight:500;letter-spacing:0.02em;background:#ffffff;
          transition:background .3s ease, color .3s ease;}
        .bs-cta:hover{background:#F5F5FA;}

        .bs-cta-arrow{width:26px;height:26px;border-radius:50%;background:#C7C9EA;color:#2E3192;
          display:grid;place-items:center;flex-shrink:0;transition:transform .35s ease;}
        .bs-card:hover .bs-cta-arrow{transform:translateX(3px);}
        .bs-cta-arrow svg{width:13px;height:13px;}

        /* ============ TABLET 640–1023 — header scaling only (cards stay fixed) ============ */
        @media (max-width:1023px){
          .bs-eyebrow{font-size:1.2vw;} .bs-title{font-size:2.6vw;}
          .bs-sub{font-size:1.3vw;line-height:2vw;}
          .bs-sub br{display:none;}
          .bs-squiggle{width:12vw;margin:0 auto;}
          .bs-grid{gap:20px;padding:0 20px;}
        }

        /* ============ MOBILE <640 — single card per row (same fixed card) ============ */
        @media (max-width:639px){
          .bs-title{font-size:5vw;}
          .bs-sub{font-size:3vw;line-height:4.6vw;}
          .bs-eyebrow{font-size:2.4vw;}
          .bs-squiggle{width:24vw;}
          .bs-grid{gap:20px;padding:0 16px;}
        }
      `}</style>

      <header className="bs-head">
        <span className="bs-eyebrow">Best Sellers</span>
        <h2 className="bs-title">Our Beauty Best Sellers</h2>
        <img className="bs-squiggle" src={vector1} alt="" aria-hidden="true" />
        <p className="bs-sub">
          Discover our best-selling beauty and skincare essentials trusted by thousands for radiant, healthy skin—
          <br/>
          crafted with premium ingredients and proven results.
        </p>
      </header>

      <div className="bs-grid">
        {bestSellers.map((p) => (
          <BestSellerCard key={p.id} p={p} />
        ))}
      </div>
    </section>
  );
}